import "./menu.css";

const NAME_KEY = "tribez:playerName";

export interface RoomRow {
  roomId: string;
  lobbyName: string;
  status: string; // "lobby" | "running" | ...
  humans: number;
  capacity: number;
  isPrivate: boolean;
  joinable: boolean;
}

export interface LandingCallbacks {
  onQuickMatch(name: string): void;
  onCreate(name: string): void;
  onJoin(roomId: string, name: string, password: string | null): void;
  onRefresh(): void;
}

export interface LandingApi {
  show(): void;
  hide(): void;
  setRooms(rooms: RoomRow[]): void;
  getName(): string;
}

/** Themed password prompt (replaces window.prompt). Resolves with the entered
 *  password, or null if cancelled (Esc / backdrop / Cancel). Reuses menu.css. */
function passwordModal(lobbyName: string): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "menu-screen";
    overlay.style.zIndex = "60";
    overlay.style.background = "rgba(0,0,0,0.55)";

    const panel = document.createElement("div");
    panel.className = "menu-panel";
    panel.style.width = "min(420px, 92vw)";

    const heading = document.createElement("div");
    heading.className = "menu-title";
    heading.style.fontSize = "20px";
    heading.textContent = "Password required";

    const sub = document.createElement("div");
    sub.className = "menu-tag";
    sub.textContent = `Enter the password for "${lobbyName}"`;

    const row = document.createElement("div");
    row.className = "menu-row";
    row.style.marginTop = "14px";
    const input = document.createElement("input");
    input.className = "menu-name-input";
    input.type = "password";
    input.placeholder = "Password";
    row.appendChild(input);

    const btns = document.createElement("div");
    btns.className = "menu-row";
    btns.style.justifyContent = "flex-end";
    btns.style.marginTop = "12px";
    const cancel = document.createElement("button");
    cancel.className = "menu-btn menu-btn--ghost";
    cancel.textContent = "Cancel";
    const ok = document.createElement("button");
    ok.className = "menu-btn";
    ok.textContent = "Join";
    btns.append(cancel, ok);

    panel.append(heading, sub, row, btns);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    input.focus();

    let done = false;
    function close(result: string | null): void {
      if (done) return;
      done = true;
      document.removeEventListener("keydown", onKey, true);
      overlay.remove();
      resolve(result);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") { e.preventDefault(); close(null); }
      else if (e.key === "Enter") { e.preventDefault(); close(input.value); }
    }
    document.addEventListener("keydown", onKey, true);
    overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(null); });
    cancel.addEventListener("click", () => close(null));
    ok.addEventListener("click", () => close(input.value));
  });
}

export function createLanding(cb: LandingCallbacks): LandingApi {
  const root = document.createElement("div");
  root.className = "menu-screen hidden";

  const panel = document.createElement("div");
  panel.className = "menu-panel";
  root.appendChild(panel);

  const title = document.createElement("h1");
  title.className = "menu-title";
  title.textContent = "TRIBEZ";
  panel.appendChild(title);

  const tag = document.createElement("div");
  tag.className = "menu-tag";
  tag.textContent = "Survive. Build. Conquer.";
  panel.appendChild(tag);

  // Name row
  const nameRow = document.createElement("div");
  nameRow.className = "menu-row";
  const nameInput = document.createElement("input");
  nameInput.className = "menu-name-input";
  nameInput.placeholder = "Your name";
  nameInput.maxLength = 20;
  nameInput.value = localStorage.getItem(NAME_KEY) ?? "";
  nameInput.addEventListener("input", () => {
    localStorage.setItem(NAME_KEY, nameInput.value);
  });
  nameRow.appendChild(nameInput);
  panel.appendChild(nameRow);

  const getName = (): string => nameInput.value.trim();

  // Action button row
  const actionRow = document.createElement("div");
  actionRow.className = "menu-row";

  const quickBtn = document.createElement("button");
  quickBtn.className = "menu-btn";
  quickBtn.textContent = "⚡ Quick Match";
  quickBtn.addEventListener("click", () => cb.onQuickMatch(getName()));
  actionRow.appendChild(quickBtn);

  const createBtn = document.createElement("button");
  createBtn.className = "menu-btn menu-btn--ghost";
  createBtn.textContent = "+ Create Lobby";
  createBtn.addEventListener("click", () => cb.onCreate(getName()));
  actionRow.appendChild(createBtn);

  const refreshBtn = document.createElement("button");
  refreshBtn.className = "menu-btn menu-btn--ghost";
  refreshBtn.textContent = "↻ Refresh";
  refreshBtn.addEventListener("click", () => cb.onRefresh());
  actionRow.appendChild(refreshBtn);

  panel.appendChild(actionRow);

  // How-to card (onboarding layer C) — static, skippable controls + goal so a
  // brand-new player knows the basics before entering. Just-in-time tips + the
  // objective ticker (hud/onboarding.ts) reinforce these in-match.
  const howto = document.createElement("div");
  howto.className = "menu-howto";
  howto.innerHTML = `
    <div class="menu-howto__hdr">How to play</div>
    <div class="menu-howto__grid">
      <div class="menu-howto__item"><b>Right-click</b><span>Move · harvest · attack</span></div>
      <div class="menu-howto__item"><b>Z</b><span>Build menu</span></div>
      <div class="menu-howto__item"><b>Q W E R</b><span>Abilities</span></div>
      <div class="menu-howto__item"><b>Space</b><span>Recenter camera</span></div>
    </div>
    <div class="menu-howto__goal">Gather resources, build your camp, survive the cold nights, and wipe out the rival tribe.</div>
  `;
  panel.appendChild(howto);

  // Room list
  const list = document.createElement("div");
  list.className = "menu-list";
  panel.appendChild(list);

  document.body.appendChild(root);

  function setRooms(rooms: RoomRow[]): void {
    list.replaceChildren();

    if (rooms.length === 0) {
      const empty = document.createElement("div");
      empty.className = "menu-list__empty";
      empty.textContent = "No open lobbies — start one with Quick Match or Create Lobby.";
      list.appendChild(empty);
      return;
    }

    for (const room of rooms) {
      const rowEl = document.createElement("div");
      rowEl.className = "menu-room";

      const nameEl = document.createElement("div");
      nameEl.className = "menu-room__name";
      // textContent avoids HTML injection from player-chosen lobby names.
      nameEl.textContent = room.lobbyName;
      if (room.isPrivate) {
        const lock = document.createElement("span");
        lock.className = "menu-room__lock";
        lock.textContent = " 🔒";
        nameEl.appendChild(lock);
      }
      rowEl.appendChild(nameEl);

      const metaEl = document.createElement("div");
      metaEl.className = "menu-room__meta";
      metaEl.textContent = `${room.humans}/${room.capacity} · ${room.status}`;
      rowEl.appendChild(metaEl);

      const joinBtn = document.createElement("button");
      joinBtn.className = "menu-btn";
      joinBtn.textContent = "Join";
      if (!room.joinable) {
        joinBtn.disabled = true;
      } else {
        joinBtn.addEventListener("click", async () => {
          let pw: string | null = null;
          if (room.isPrivate) {
            pw = await passwordModal(room.lobbyName);
            if (pw === null || pw === "") return; // cancelled / empty
          }
          cb.onJoin(room.roomId, getName(), pw);
        });
      }
      rowEl.appendChild(joinBtn);

      list.appendChild(rowEl);
    }
  }

  return {
    show: () => root.classList.remove("hidden"),
    hide: () => root.classList.add("hidden"),
    setRooms,
    getName,
  };
}
